import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  User,
  DollarSign,
  Search,
  RefreshCw,
  FileText,
  ChevronRight,
  ChevronLeft,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import agentInsurancePurchasesService from "../../api/AgentInsurancePurchases";
import AuthenticatedFooter from "../../components/landing/AuthenticatedFooter";
import Images from "../../utils/Images";

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

function formatCurrency(value, currency = "GHS") {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  const symbol = String(currency || "GHS").toUpperCase() === "USD" ? "$" : "GH₵";
  return `${symbol} ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateString) {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(dateString);
  }
}

/** Map API item to display fields. Adjust when backend response shape is known. */
function getItemDisplay(item) {
  const clientName =
    item.client_name ??
    item.customer_name ??
    ([item.first_name, item.last_name].filter(Boolean).join(" ").trim() || item.registrant_name) ??
    "—";
  const policyLabel =
    item.policy_type ?? item.policy_name ?? item.product_type ?? item.insurance_type ?? "Insurance";
  return {
    policyType: policyLabel,
    customerName: clientName,
    amount: item.premium ?? item.amount ?? item.total ?? item.total_premium,
    currency: item.currency ?? "GHS",
    status: item.status ?? item.payment_status ?? "—",
    startDate: item.start_date ?? item.effective_date ?? item.created_at,
    endDate: item.end_date ?? item.expiry_date,
    purchasedAt: item.created_at ?? item.purchased_at,
    policyNumber: item.policy_number ?? item.reference ?? item.id,
    id: item.id ?? item.slug ?? item.policy_number,
    slug: item.slug,
  };
}

function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-2xl border-2 border-gray-200 bg-white shadow-lg">
      <div className="p-6">
        <div className="flex gap-4">
          <div className="h-14 w-14 flex-shrink-0 rounded-xl bg-gray-200 animate-pulse" />
          <div className="min-w-0 flex-1 space-y-3">
            <div className="h-5 w-3/4 rounded bg-gray-200 animate-pulse" />
            <div className="h-4 w-1/2 rounded bg-gray-100 animate-pulse" />
            <div className="flex gap-4">
              <div className="h-4 w-24 rounded bg-gray-100 animate-pulse" />
              <div className="h-4 w-20 rounded bg-gray-100 animate-pulse" />
            </div>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <div className="h-8 w-24 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-8 w-20 rounded-lg bg-gray-100 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

export default function InsurancePurchases() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const fetchPurchases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await agentInsurancePurchasesService.getAllInsurancePurchases();
      const items = response.list ?? [];
      if (response.status) {
        setList(Array.isArray(items) ? items : []);
      } else {
        setError(response.message || "Failed to load insurance purchases");
        setList([]);
        toast.error(response.message || "Failed to load insurance purchases");
      }
    } catch (err) {
      setError(err?.message || "An error occurred");
      setList([]);
      toast.error(err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().trim();
    return list.filter((item) => {
      const d = getItemDisplay(item);
      return (
        d.policyType.toLowerCase().includes(q) ||
        d.customerName.toLowerCase().includes(q) ||
        (d.policyNumber && String(d.policyNumber).toLowerCase().includes(q)) ||
        (d.status && String(d.status).toLowerCase().includes(q))
      );
    });
  }, [list, searchQuery]);

  const totalFiltered = filteredList.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const paginatedList = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredList.slice(start, start + pageSize);
  }, [filteredList, safePage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  const heroImages = [Images.hero_img_web_three, Images.hero_img_web_four].filter(Boolean);
  const heroSlides = heroImages.length ? heroImages : [Images.hero_img_web_one];

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Hero header - consistent with PurchasedEvents */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 shadow-2xl">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>
        <div className="relative px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <button
              type="button"
              onClick={() => navigate("/insurance/choose")}
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/20 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
            >
              <ChevronRight className="rotate-180" size={18} />
              Back to Insurance
            </button>
            <div className="mt-6 flex gap-4 items-center">
              <div className="flex justify-center items-center w-16 h-16 rounded-2xl border-2 border-white/30 bg-white/20 shadow-xl backdrop-blur-md">
                <Shield size={32} className="text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-extrabold text-white drop-shadow-lg sm:text-3xl">
                  Insurance purchases
                </h1>
                <p className="mt-1 text-sm text-indigo-100 sm:text-base">
                  Policies sold by you to your clients
                </p>
              </div>
            </div>
            <div className="mt-2 h-1 w-24 rounded bg-white/60" />
          </div>
        </div>
      </div>

      <section className="flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="text-sm text-gray-600 max-w-xl">
                All insurance policies you have sold. Data is loaded from the agent policy API.
              </p>
            </div>
            <motion.button
              type="button"
              onClick={fetchPurchases}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2.5 text-sm font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-50 disabled:opacity-60"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </motion.button>
          </motion.div>

          {list.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="relative mb-6"
            >
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by policy type, client name, or status..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 bg-white py-3 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {[1, 2, 3, 4, 5].map((i) => (
                  <SkeletonCard key={i} />
                ))}
              </motion.div>
            ) : error ? (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center rounded-2xl border-2 border-amber-200 bg-amber-50/80 py-16 px-6"
              >
                <AlertCircle className="mb-4 h-14 w-14 text-amber-500" />
                <h3 className="mb-2 text-lg font-bold text-amber-900">Unable to load purchases</h3>
                <p className="mb-6 max-w-md text-center text-sm text-amber-800">{error}</p>
                <motion.button
                  type="button"
                  onClick={fetchPurchases}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-amber-600"
                >
                  Try again
                </motion.button>
              </motion.div>
            ) : filteredList.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50/80 py-20 px-6"
              >
                <Shield className="mb-4 h-16 w-16 text-gray-400" />
                <h3 className="mb-2 text-xl font-bold text-gray-700">
                  {searchQuery.trim() ? "No matching policies" : "No insurance purchases yet"}
                </h3>
                <p className="max-w-md text-center text-sm text-gray-600">
                  {searchQuery.trim()
                    ? "Try a different search term."
                    : "Insurance policies you sell will appear here."}
                </p>
                {searchQuery.trim() && (
                  <motion.button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="mt-6 text-sm font-semibold text-blue-600 hover:text-blue-700"
                  >
                    Clear search
                  </motion.button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {paginatedList.map((item, index) => {
                  const d = getItemDisplay(item);
                  const hasAmount = d.amount != null && d.amount !== "";
                  const isActive =
                    String(d.status || "").toLowerCase() === "active" ||
                    String(d.status || "").toLowerCase() === "paid" ||
                    String(d.status || "").toLowerCase() === "completed";
                  return (
                    <motion.div
                      key={d.id ?? index}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.04 }}
                      className="overflow-hidden rounded-2xl border-2 border-gray-200 bg-white shadow-lg transition-all hover:border-blue-200 hover:shadow-xl"
                    >
                      <div className="flex flex-col sm:flex-row">
                        <div className="flex h-36 w-full flex-shrink-0 items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 sm:h-auto sm:w-44">
                          <Shield className="h-12 w-12 text-white/90" />
                        </div>
                        <div className="flex flex-1 flex-col p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
                          <div className="min-w-0 flex-1">
                            <h3 className="truncate text-lg font-bold text-gray-900">
                              {d.policyType}
                            </h3>
                            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Policy type
                            </p>
                            <div className="mt-2 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                                <User size={18} className="text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                                  Purchased for
                                </span>
                                <p className="truncate text-sm font-semibold text-gray-900">
                                  {d.customerName}
                                </p>
                              </div>
                            </div>
                            {d.policyNumber && (
                              <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
                                <FileText size={14} className="flex-shrink-0 text-gray-400" />
                                <span>
                                  Ref: <span className="font-mono font-medium text-gray-700">{d.policyNumber}</span>
                                </span>
                              </p>
                            )}
                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                              {d.startDate && (
                                <span className="inline-flex items-center gap-1">
                                  <Calendar size={12} />
                                  From {formatDate(d.startDate)}
                                </span>
                              )}
                              {d.endDate && (
                                <span className="inline-flex items-center gap-1">
                                  <Calendar size={12} />
                                  To {formatDate(d.endDate)}
                                </span>
                              )}
                              {d.purchasedAt && !d.startDate && (
                                <span className="inline-flex items-center gap-1">
                                  <Clock size={12} />
                                  Purchased {formatDate(d.purchasedAt)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-4 flex flex-shrink-0 flex-wrap items-center gap-3 sm:mt-0 sm:flex-col sm:items-end">
                            {hasAmount && (
                              <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1.5">
                                <DollarSign size={16} className="text-blue-600" />
                                <span className="text-base font-bold text-blue-700">
                                  {formatCurrency(d.amount, d.currency)}
                                </span>
                              </div>
                            )}
                            {d.status && (
                              <span
                                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${
                                  isActive
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-amber-200 bg-amber-50 text-amber-700"
                                }`}
                              >
                                {isActive ? (
                                  <CheckCircle2 size={12} />
                                ) : (
                                  <Clock size={12} />
                                )}
                                {String(d.status).replace(/_/g, " ")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {!loading && !error && filteredList.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-8 flex flex-col gap-4 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-gray-600">
                  Showing{" "}
                  <span className="font-semibold text-gray-900">
                    {(safePage - 1) * pageSize + 1}–
                    {Math.min(safePage * pageSize, totalFiltered)}
                  </span>{" "}
                  of <span className="font-semibold text-gray-900">{totalFiltered}</span>
                  {searchQuery.trim() && list.length !== filteredList.length && (
                    <span className="text-gray-500"> (filtered from {list.length})</span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="insurance-page-size" className="text-sm text-gray-500">
                    Per page
                  </label>
                  <select
                    id="insurance-page-size"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <motion.button
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  whileHover={safePage > 1 ? { scale: 1.02 } : {}}
                  whileTap={safePage > 1 ? { scale: 0.98 } : {}}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 hover:border-gray-400"
                >
                  <ChevronLeft size={18} />
                  Previous
                </motion.button>
                <span className="px-2 text-sm text-gray-600">
                  Page <span className="font-semibold text-gray-900">{safePage}</span> of{" "}
                  <span className="font-semibold text-gray-900">{totalPages}</span>
                </span>
                <motion.button
                  type="button"
                  disabled={safePage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  whileHover={safePage < totalPages ? { scale: 1.02 } : {}}
                  whileTap={safePage < totalPages ? { scale: 0.98 } : {}}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50 hover:border-gray-400"
                >
                  Next
                  <ChevronRight size={18} />
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <AuthenticatedFooter />
    </div>
  );
}
